import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchAllTechniquesAction } from '../../../utils/redux/techniques/techniquesAction';
import { fetchAllTechniqueCategories } from '../../../utils/redux/techniqueCategory/techniqueCategoryActions';
import { Container, Table, Badge, Form, Row, Col, InputGroup, Button, Card } from 'react-bootstrap';
import { useNavigate, useSearchParams } from 'react-router-dom';
// import './TechniquePage.css';
import AddTechniqueModal from '../../modals/AddTechniqueModal';
import TechniqueCategoryModal from '../../modals/TechniqueCategoryModal';
import '../CSS/common.css';

const TechniquePage = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { techniques, isLoading, totalTechniques, totalPages } = useSelector((state) => state.techniques);
    const { techniqueCategories } = useSelector((state) => state.techniqueCategory);
    const [params] = useSearchParams();

    // Search and filter states
    const [searchQuery, setSearchQuery] = useState(params.get('searchQuery') || '');
    const [category, setCategory] = useState(params.get('category') || '');
    const [sortBy, setSortBy] = useState('createdAt');
    const [limit, setLimit] = useState(10);
    const [page, setPage] = useState(params.get('page') ? parseInt(params.get('page')) : 1);
    const [summaryExist, setSummaryExist] = useState(params.get('summaryExist') || '');

    // Modal states
    const [showAddModal, setShowAddModal] = useState(false);
    const [showCatModal, setShowCatModal] = useState(false);

    useEffect(() => {
        const param = {
            search: searchQuery,
            category: category,
            sortBy: sortBy,
            limit: limit,
            page: params.get('page') ? parseInt(params.get('page')) : page,
            summaryExist: summaryExist
        };
        dispatch(fetchAllTechniquesAction(param));
        dispatch(fetchAllTechniqueCategories());
        // eslint-disable-next-line
    }, []);

    const handleResetFilters = () => {
        setCategory('');
        setSearchQuery('');
        setPage(1);
        setSummaryExist('');
        navigate(`/techniques?page=1`);
        dispatch(fetchAllTechniquesAction());
    };

    const handlePageChange = (page) => {
        setPage(page);
        navigate(`/techniques?searchQuery=${searchQuery}&category=${category}&summaryExist=${summaryExist}&page=${page}`);
        const param = {
            search: searchQuery,
            category: category,
            sortBy: sortBy,
            limit: limit,
            page: page,
            summaryExist: summaryExist
        };
        dispatch(fetchAllTechniquesAction(param));
    };

    // Handle search on enter press
    const handleSearch = (e) => {
        if (e.key === 'Enter') {
            navigate(`/techniques?searchQuery=${searchQuery}&category=${category}&summaryExist=${summaryExist}&page=1`);
            setPage(1);
            const param = {
                search: searchQuery,
                category: category,
                sortBy: sortBy,
                limit: limit,
                page: 1,
                summaryExist: summaryExist
            };
            dispatch(fetchAllTechniquesAction(param));
        }
    };

    const handleSearchButtonClick = () => {
        navigate(`/techniques?searchQuery=${searchQuery}&category=${category}&summaryExist=${summaryExist}&page=1`);
        setPage(1);
        const param = {
            search: searchQuery,
            category: category,
            sortBy: sortBy,
            limit: limit,
            page: 1,
            summaryExist: summaryExist
        };
        dispatch(fetchAllTechniquesAction(param));
    }

    // Handle category change
    const handleCategoryChange = (e) => {
        setCategory(e.target.value);
        navigate(`/techniques?searchQuery=${searchQuery}&category=${e.target.value}&summaryExist=${summaryExist}&page=1`);
        setPage(1);
        dispatch(fetchAllTechniquesAction({
            search: searchQuery,
            category: e.target.value,
            sortBy: sortBy,
            limit: limit,
            page: 1,
            summaryExist: summaryExist
        }));
    };

    // Handle sort change
    const handleSortChange = (e) => {
        setSortBy(e.target.value);
        setPage(1);
        dispatch(fetchAllTechniquesAction({
            search: searchQuery,
            category: category,
            sortBy: e.target.value,
            limit: limit,
            page: 1,
            summaryExist: summaryExist
        }));
    };

    const formatDate = (dateString) => {
        if (!dateString) return "No Date";
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
        });
    };

    const getPageNumbers = (current, total) => {
        if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
        if (current <= 4) {
            return [1, 2, 3, 4, 5, '...', total];
        }
        if (current >= total - 3) {
            return [1, '...', total - 4, total - 3, total - 2, total - 1, total];
        }
        return [1, '...', current - 1, current, current + 1, '...', total];
    };

    if (isLoading) {
        return <div className="text-center mt-5">Loading...</div>;
    }

    return (
        <Container className="py-4">
            <div className="d-flex flex-wrap align-items-center justify-content-between mb-4">
                <h2 className="mb-0">Available Techniques</h2>
                <div className="d-flex gap-2">
                    <Button variant="primary" onClick={() => setShowAddModal(true)}>
                        + Add Technique
                    </Button>
                    <Button variant="outline-primary" onClick={() => setShowCatModal(true)}>
                        Manage Categories
                    </Button>
                </div>
            </div>

            <Card className="mb-4 shadow-sm border-0">
                <Card.Body>
                    <Row className="gy-3 gx-3 align-items-end">
                        <Col md={4} xs={12}>
                            <Form.Label htmlFor="searchInput" className="fw-semibold">Search</Form.Label>
                            <InputGroup>
                                <Form.Control
                                    id="searchInput"
                                    placeholder="Search techniques..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    onKeyPress={handleSearch}
                                />
                            </InputGroup>
                            <Button
                                className='mt-2'
                                onClick={handleSearchButtonClick}
                            >
                                Search
                            </Button>
                        </Col>
                        <Col md={3} xs={6}>
                            <Form.Label className="fw-semibold">Category</Form.Label>
                            <Form.Select
                                value={category}
                                onChange={handleCategoryChange}
                            >
                                <option value="">All Categories</option>
                                {techniqueCategories?.map(cat => (
                                    <option key={cat._id} value={cat._id}>{cat?.name}</option>
                                ))}
                            </Form.Select>
                        </Col>
                        <Col md={2} xs={6}>
                            <Form.Label className="fw-semibold">Sort By</Form.Label>
                            <Form.Select
                                value={sortBy}
                                onChange={handleSortChange}
                            >
                                <option value="createdAt">Created Date</option>
                                <option value="title">Title</option>
                                <option value="duration">Duration</option>
                            </Form.Select>
                        </Col>
                        <Col md={2} xs={6}>
                            <Form.Label className="fw-semibold">Summary</Form.Label>
                            <Form.Select
                                value={summaryExist}
                                onChange={e => {
                                    setSummaryExist(e.target.value);
                                    setPage(1);
                                    navigate(`/techniques?searchQuery=${searchQuery}&category=${category}&summaryExist=${e.target.value}&page=1`);
                                    dispatch(fetchAllTechniquesAction({
                                        search: searchQuery,
                                        category: category,
                                        sortBy: sortBy,
                                        limit: limit,
                                        page: 1,
                                        summaryExist: e.target.value
                                    }));
                                }}
                            >
                                <option value="">All</option>
                                <option value="true">Exists</option>
                                <option value="false">Not Exists</option>
                            </Form.Select>
                        </Col>
                        <Col md={1} xs={12} className="d-flex gap-2 justify-content-end align-items-center mt-md-0 mt-3">
                            <Button variant="secondary" onClick={handleResetFilters}>
                                Reset
                            </Button>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            <Card>
                <Card.Body>
                    <Row className='d-flex flex-row justify-content-start align-items-center fit-content'>
                        <span className='w-auto'>Sheet Link:</span>
                        <a
                            href='https://docs.google.com/spreadsheets/d/146EByLdSfQSX7MCqcERhdiHvkEvHyg-jbYZIj_9V90c/edit?usp=sharing' target='_blank'
                            className='w-auto'
                        >
                            Techniques Data
                        </a>
                    </Row>
                </Card.Body>
            </Card>

            {/* Table View */}
            <div className="d-none d-md-block">
                <Table hover responsive className="talk-table">
                    <colgroup>
                        <col style={{ width: '30%' }} />
                        <col style={{ width: '30%' }} />
                        <col style={{ width: '10%' }} />
                        <col style={{ width: '30%' }} />
                    </colgroup>
                    <thead>
                        <tr>
                            <th>Title</th>
                            <th>Category</th>
                            <th>Duration</th>
                            <th>Keywords</th>
                        </tr>
                    </thead>
                    <tbody>
                        {techniques?.map((tech) => (
                            <tr
                                key={tech._id}
                                className="talk-row"
                                onClick={() => navigate(`/techniques/${tech._id}`)}
                                style={{ cursor: 'pointer' }}
                            >
                                <td>
                                    <div className="title-cell">
                                        <strong title={tech.title}>{tech.title}</strong>
                                    </div>
                                </td>
                                <td>{tech?.categories?.map(cat => `${cat?.name}, `) || "No Category"}</td>
                                <td>{tech.durationHhmmss || "-"}</td>
                                <td>
                                    <div className="keywords-cell">
                                        {tech?.keywords?.map((keyword, index) => (
                                            <Badge
                                                key={index}
                                                bg="secondary"
                                                className="me-1 mb-1"
                                                style={{
                                                    maxWidth: '100%',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis'
                                                }}
                                                title={keyword}
                                            >
                                                {keyword}
                                            </Badge>
                                        ))}
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            </div>

            {/* Mobile Card View */}
            <div className="d-md-none">
                <div className="talk-cards">
                    {techniques?.map((tech) => (
                        <div
                            key={tech._id}
                            className="talk-card-mobile"
                            onClick={() => navigate(`/techniques/${tech._id}`)}
                        >
                            <h5 className="talk-card-title" title={tech.title}>
                                {tech.title}
                            </h5>
                            <div className="talk-card-meta">
                                <span className="meta-item">
                                    <small className="text-muted">Category:</small> {tech.categories?.map(cat => `${cat?.name}, `) || "No Category"}
                                </span>
                                <span className="meta-item">
                                    <small className="text-muted">Duration:</small> {tech.durationHhmmss || "-"}
                                </span>
                            </div>
                            <div className="talk-card-keywords">
                                {tech?.keywords?.map((keyword, index) => (
                                    <Badge
                                        key={index}
                                        bg="secondary"
                                        className="me-1 mb-1"
                                        title={keyword}
                                    >
                                        {keyword}
                                    </Badge>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
            <div className='d-flex flex-column justify-content-center align-items-center w-100'>
                <div className='mt-1 mb-3'>
                    Showing {(limit * (page - 1)) + 1} - {totalTechniques < limit * (page) ? totalTechniques : limit * (page)} of {totalTechniques} Techniques
                </div>
                <div className="pagination-controls">
                    <button
                        className="pagination-button"
                        onClick={() => handlePageChange(1)}
                        disabled={page === 1 || isLoading}
                    >
                        First
                    </button>
                    <button
                        className="pagination-button"
                        onClick={() => handlePageChange(page - 1)}
                        disabled={page === 1 || isLoading}
                    >
                        Previous
                    </button>
                    <div className="page-numbers">
                        {getPageNumbers(page, totalPages).map((pageind, index) => (
                            <React.Fragment key={index}>
                                {pageind === '...' ? (
                                    <span className="pagination-ellipsis">...</span>
                                ) : (
                                    <button
                                        className={`pagination-button ${page === pageind ? 'active' : ''}`}
                                        onClick={() => handlePageChange(pageind)}
                                        disabled={isLoading}
                                    >
                                        {pageind}
                                    </button>
                                )}
                            </React.Fragment>
                        ))}
                    </div>
                    <button
                        className="pagination-button"
                        onClick={() => handlePageChange(page + 1)}
                        disabled={page === totalPages || isLoading}
                    >
                        Next
                    </button>
                    <button
                        className="pagination-button"
                        onClick={() => handlePageChange(totalPages)}
                        disabled={page === totalPages || isLoading}
                    >
                        Last
                    </button>
                </div>
            </div>
            <AddTechniqueModal
                show={showAddModal}
                handleClose={() => setShowAddModal(false)}
                techniqueCategories={techniqueCategories}
                onSuccess={() => dispatch(fetchAllTechniquesAction())}
            />
            <TechniqueCategoryModal show={showCatModal} handleClose={() => setShowCatModal(false)} />
        </Container>
    );
};

export default TechniquePage;